
import type { MealPlan, Recipe, PlannedMeal } from '../types';
import { unifyUnit } from './unitConversion';
import { DAYS_OF_WEEK } from '../types';

/** Convert a single ingredient quantity to grams (unscaled). */
export function ingredientQuantityToGrams(quantity: number, unit: string): number {
    const u = unifyUnit(unit || '');
    if (u === 'kg') return quantity * 1000;
    if (u === 'g') return quantity;
    if (u === 'lb') return quantity * 453.59;
    if (u === 'oz') return quantity * 28.35;
    return quantity * 200;
}

/** Meat weight in grams for a recipe, scaled by planned servings and optional repeat count. */
export function calculateScaledMeatGrams(
    recipe: Recipe,
    plannedServings?: number,
    repeatCount: number = 1,
): number {
    const recipeServings = recipe.servings > 0 ? recipe.servings : 1;
    const ratio = (plannedServings ?? recipeServings) / recipeServings;
    const days = Math.max(1, repeatCount);
    let totalGrams = 0;

    for (const ing of recipe.ingredients) {
        if (!ing.isMeat) continue;
        totalGrams += ingredientQuantityToGrams(ing.quantity * ratio, ing.unit);
    }

    return Math.round(totalGrams * days * 10) / 10;
}

export function buildScaledMeatItems(
    recipe: Recipe,
    plannedServings?: number,
): { name: string; quantity: number; unit: string; recipeName: string }[] {
    const recipeServings = recipe.servings > 0 ? recipe.servings : 1;
    const ratio = (plannedServings ?? recipeServings) / recipeServings;

    return recipe.ingredients
        .filter((ing) => ing.isMeat)
        .map((ing) => ({
            name: ing.name,
            quantity: Math.round(ingredientQuantityToGrams(ing.quantity * ratio, ing.unit) * 10) / 10,
            unit: 'g',
            recipeName: recipe.name,
        }));
}

/**
 * Iterates through every day -> every meal -> every ingredient to calculate total meat weight.
 */
export const calculateMonthlyMeat = (
    weeks: Date[][],
    mealPlans: Record<string, MealPlan>,
    recipes: Recipe[],
    targetMonthIndex: number
): number => {
    let totalGrams = 0;

    const formatDateKey = (date: Date): string => {
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const d = String(date.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
    };

    weeks.forEach(weekDates => {
        // Week key is usually the first day of the week (Monday or Sunday) based on plan structure
        // Assuming mealPlans are keyed by the start date string of the week
        if (weekDates.length === 0) return;
        
        // Find the plan corresponding to this week
        // Note: The key in mealPlans matches the weekStartDate field.
        // We need to match the logic used in the planner to find the plan key.
        // Usually week[0] is the start.
        const weekKey = formatDateKey(weekDates[0]);
        const plan = mealPlans[weekKey];

        if (!plan) return;

        weekDates.forEach(date => {
            // Only count days belonging to the target month
            if (date.getMonth() !== targetMonthIndex) return;

            const dayName = DAYS_OF_WEEK[date.getDay()];
            const meals = plan[dayName];

            if (meals && meals.length > 0) {
                meals.forEach((meal: PlannedMeal) => {
                    const recipe = recipes.find(r => r.id === meal.recipeId);
                    if (!recipe) return;

                    const ratio = meal.plannedServings / recipe.servings;

                    recipe.ingredients.forEach(ing => {
                        if (ing.isMeat) {
                            totalGrams += ingredientQuantityToGrams(ing.quantity * ratio, ing.unit);
                        }
                    });
                });
            }
        });
    });

    return totalGrams;
};